import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  EnvelopeSimpleIcon,
  FacebookLogoIcon,
  LinkedinLogoIcon,
  LinkSimpleIcon,
  PrinterIcon,
  ShareNetworkIcon,
  TelegramLogoIcon,
  WhatsappLogoIcon,
  XLogoIcon,
} from '@phosphor-icons/react';
import { SocialIcon } from 'volto-ministerio-publico-federal/components/Icons/social';
import {
  usePrintPage,
  useSharePage,
} from 'volto-ministerio-publico-federal/hooks';
import { SHARE_NETWORKS, type ShareNetworkToken } from './networks';

// Componentes de ícone do Phosphor: `React.ElementType` evita atritar com o
// tipo `Icon` deles (que declara props além de `size`/`aria-hidden`).
const POPOVER_ICONS: Record<ShareNetworkToken, React.ElementType> = {
  facebook: FacebookLogoIcon,
  whatsapp: WhatsappLogoIcon,
  x: XLogoIcon,
  linkedin: LinkedinLogoIcon,
  telegram: TelegramLogoIcon,
  email: EnvelopeSimpleIcon,
};

interface ShareBarProps {
  /** Título da notícia, usado no texto do compartilhamento. */
  title: string;
}

/**
 * Régua de compartilhamento do cabeçalho da notícia.
 *
 * Quatro botões, como no design: Facebook, WhatsApp, "Compartilhar" e
 * Imprimir. O terceiro abre um popover com as demais redes — foi o pedido
 * registrado no Figma ("incluir outra(s) redes, além do facebook"). Em
 * aparelhos com `navigator.share`, o mesmo botão chama a folha nativa, que é o
 * caminho que o usuário de celular espera.
 */
export const ShareBar: React.FC<ShareBarProps> = ({ title }) => {
  // A URL só existe no cliente: no SSR os `href` sairiam vazios e são
  // preenchidos na hidratação.
  const [shareUrl, setShareUrl] = useState('');
  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);

  const [aberto, setAberto] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handlePrint = usePrintPage();
  const handleNativeShare = useSharePage(title);

  const fechar = useCallback(() => setAberto(false), []);

  useEffect(() => {
    if (!aberto) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') fechar();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) fechar();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [aberto, fechar]);

  const byToken = (token: ShareNetworkToken) =>
    SHARE_NETWORKS.find((network) => network.token === token);

  const facebook = byToken('facebook');
  const whatsapp = byToken('whatsapp');

  const handleShareClick = () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      handleNativeShare();
      return;
    }
    setAberto((valor) => !valor);
  };

  const copiarLink = () => {
    handleNativeShare();
    fechar();
  };

  return (
    <div className="mpf-news__social">
      {facebook ? (
        <a
          href={facebook.buildUrl(shareUrl, title)}
          className="mpf-news__social-btn"
          target="_blank"
          rel="noopener noreferrer"
          data-tooltip="Compartilhar no Facebook"
          aria-label="Compartilhar no Facebook"
        >
          <SocialIcon
            network="facebook"
            variant="brand"
            size={32}
            className="mpf-news__social-icon"
          />
        </a>
      ) : null}

      {whatsapp ? (
        <a
          href={whatsapp.buildUrl(shareUrl, title)}
          className="mpf-news__social-btn"
          target="_blank"
          rel="noopener noreferrer"
          data-tooltip="Compartilhar no WhatsApp"
          aria-label="Compartilhar no WhatsApp"
        >
          <SocialIcon
            network="whatsapp"
            variant="brand"
            size={32}
            className="mpf-news__social-icon"
          />
        </a>
      ) : null}

      <div className="mpf-news__social-menu" ref={wrapperRef}>
        <button
          type="button"
          onClick={handleShareClick}
          className="mpf-news__social-btn"
          data-tooltip="Compartilhar"
          aria-label="Compartilhar"
          aria-haspopup="menu"
          aria-expanded={aberto}
        >
          <ShareNetworkIcon size={32} aria-hidden="true" />
        </button>

        {aberto ? (
          <div className="mpf-news__social-popover" role="menu">
            {SHARE_NETWORKS.map((network) => {
              const Icon = POPOVER_ICONS[network.token];
              return (
                <a
                  key={network.token}
                  role="menuitem"
                  href={network.buildUrl(shareUrl, title)}
                  className="mpf-news__social-popover-item"
                  target={network.token === 'email' ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  onClick={fechar}
                >
                  <Icon size={24} aria-hidden={true} />
                  <span>{network.label}</span>
                </a>
              );
            })}
            <button
              type="button"
              role="menuitem"
              className="mpf-news__social-popover-item"
              onClick={copiarLink}
            >
              <LinkSimpleIcon size={24} aria-hidden="true" />
              <span>Copiar link</span>
            </button>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={handlePrint}
        className="mpf-news__social-btn"
        data-tooltip="Imprimir"
        aria-label="Imprimir"
      >
        <PrinterIcon size={32} aria-hidden="true" />
      </button>
    </div>
  );
};

export default ShareBar;
